import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { Coffee, Coins, ArrowDown } from "lucide-react";

export const ShreeRound = () => {
  return (
    <section id="shreeround" data-testid="shreeround" className="relative bg-ivory-light py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <SectionHeading
            overline="ShreeRound"
            title="Turn spare change into gold"
            accentWord="spare change"
            subtitle="Every time you spend, Shreedhan rounds up to the nearest ₹10 and quietly buys gold with the difference. Save without noticing — grow without trying."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-6">
              {[["₹1,240", "average saved / month"], ["3–8g", "gold added / year"], ["0 effort", "fully automatic"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-serif text-3xl text-charcoal">{n}</p>
                  <p className="mt-1 text-sm text-charcoal-soft">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Live example */}
        <Reveal delay={0.1}>
          <div className="glass-card relative mx-auto w-full max-w-sm rounded-3xl p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Live example</p>

            {/* purchase */}
            <div className="mt-5 flex items-center justify-between rounded-2xl border border-amber-200/50 bg-white/70 p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-charcoal/5">
                  <Coffee className="h-5 w-5 text-charcoal-soft" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-charcoal">Chai & samosa</p>
                  <p className="text-xs text-charcoal-soft">Corner Cafe</p>
                </div>
              </div>
              <p className="font-serif text-lg text-charcoal">₹47</p>
            </div>

            {/* roundup arrow */}
            <div className="my-3 flex flex-col items-center gap-1">
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="flex items-center gap-1.5 rounded-full bg-gold/12 px-3 py-1 text-xs font-semibold text-gold"
              >
                <ArrowDown className="h-3.5 w-3.5" /> Round up to ₹50 · +₹3
              </motion.div>
            </div>

            {/* gold vault */}
            <div className="flex items-center justify-between rounded-2xl border border-gold/40 bg-gradient-to-r from-gold/10 to-gold-rose/10 p-4">
              <div className="flex items-center gap-3">
                <motion.span
                  animate={{ rotate: [0, -8, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gold to-gold-amber"
                >
                  <Coins className="h-5 w-5 text-charcoal-deep" />
                </motion.span>
                <div>
                  <p className="text-sm font-semibold text-charcoal">₹3 → Gold vault</p>
                  <p className="text-xs text-charcoal-soft">0.0004g added · 999.9 fine</p>
                </div>
              </div>
              <span className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.2)]" />
            </div>

            <p className="mt-5 text-center text-xs text-charcoal-soft">
              47 spends this week · <span className="font-semibold text-gold">₹214 rounded into gold</span>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
